import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import useSWR from "swr";
import api from "@/lib/api";
import { gbp, ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Users, Package, MapPin, TrendingUp, CalendarRange,
  FileText, Loader2, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const fetcher = (u) => api.get(u).then((r) => r.data);

const statusStyles = {
  open: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-700 border-slate-200",
  paid: "bg-blue-100 text-blue-700 border-blue-200",
};

const PayPeriodDetail = () => {
  const { id } = useParams();
  const { data, error, isLoading, mutate: reload } = useSWR(
    `/pay-periods/${id}/detail`, fetcher
  );
  const [openDrivers, setOpenDrivers] = useState({});
  const [busy, setBusy] = useState(false);

  const generateInvoices = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/invoices/generate?period_id=${id}`);
      toast.success(`Generated ${r.count} invoice(s) for this period`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generate failed");
    }
    setBusy(false);
  };

  if (isLoading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">Failed to load period.</div>;
  if (!data) return null;

  const { period, totals, driver_groups } = data;

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/admin/pay-periods">
          <Button variant="ghost" size="sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> All periods
          </Button>
        </Link>
      </div>

      {/* Header */}
      <Card className="p-5 md:p-7 rounded-2xl border-brand-border bg-brand-navy text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono flex items-center gap-2">
              <CalendarRange className="w-3 h-3" />
              Pay period detail
            </div>
            <div className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
              {period.name}
            </div>
            <div className="text-xs text-white/70 mt-1.5">
              {ddmmyyyy(period.start_date)} → {ddmmyyyy(period.end_date)} · Pays {ddmmyyyy(period.payment_date)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`uppercase tracking-wider text-[10px] border-white/40 text-white bg-white/10 ${period.status}`}>
              {period.status}
            </Badge>
            <Button onClick={generateInvoices} disabled={busy}
                    className="bg-brand-red hover:bg-brand-red-hover" data-testid="generate-invoices-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
              Generate invoices
            </Button>
          </div>
        </div>
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Drivers", value: totals.drivers, icon: Users, color: "text-indigo-600 bg-indigo-50" },
          { label: "Work days", value: totals.days, icon: CalendarRange, color: "text-emerald-600 bg-emerald-50" },
          { label: "Stops", value: (totals.stops || 0).toLocaleString("en-GB"), icon: MapPin, color: "text-brand-navy bg-slate-100" },
          { label: "Parcels", value: (totals.parcels || 0).toLocaleString("en-GB"), icon: Package, color: "text-amber-600 bg-amber-50" },
          { label: totals.gross != null ? "Gross" : "Net", value: totals.gross != null ? gbp(totals.gross) : "—", icon: TrendingUp, color: "text-brand-red bg-brand-red/10" },
        ].map((kpi) => (
          <Card key={kpi.label} className="p-4 rounded-2xl border-brand-border">
            <div className={`w-9 h-9 rounded-xl grid place-items-center ${kpi.color}`}>
              <kpi.icon className="w-4 h-4" />
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-500">{kpi.label}</div>
            <div className="font-heading text-xl font-bold text-brand-text">{kpi.value ?? 0}</div>
          </Card>
        ))}
      </div>

      {/* Driver groups */}
      <div className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-red font-mono">Per driver breakdown</div>
        {driver_groups.length === 0 && (
          <Card className="p-8 text-center text-sm text-slate-500 rounded-2xl">
            No work rows in this period yet.
          </Card>
        )}
        {driver_groups.map((g) => {
          const isOpen = !!openDrivers[g.driver_id];
          return (
            <Card key={g.driver_id} className="rounded-2xl border-brand-border overflow-hidden"
                  data-testid={`period-driver-${g.driver_id}`}>
              <button
                type="button"
                onClick={() => setOpenDrivers((s) => ({ ...s, [g.driver_id]: !s[g.driver_id] }))}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
              >
                <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{g.driver_code || "—"}</div>
                  <div className="font-heading font-bold text-brand-text truncate">{g.driver_name || "(unknown driver)"}</div>
                </div>
                <div className="hidden sm:flex items-center gap-5 text-right">
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Days</div><div className="font-heading text-sm font-bold">{g.days}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Stops</div><div className="font-heading text-sm font-bold">{g.stops}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Parcels</div><div className="font-heading text-sm font-bold">{g.parcels}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Miles</div><div className="font-heading text-sm font-bold">{Math.round(g.mileage || 0)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Gross</div><div className="font-heading text-sm font-bold text-brand-red">{g.gross != null ? gbp(g.gross) : "—"}</div></div>
                </div>
                <Link to={`/admin/drivers/${g.driver_id}`} onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm" data-testid={`open-driver-${g.driver_id}`}>Open driver</Button>
                </Link>
              </button>
              {isOpen && (
                <div className="border-t border-brand-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
                      <tr>
                        <th className="text-left px-4 py-2.5">Date</th>
                        <th className="text-left px-4 py-2.5">Route</th>
                        <th className="text-right px-4 py-2.5">Stops</th>
                        <th className="text-right px-4 py-2.5">Parcels</th>
                        <th className="text-right px-4 py-2.5">Miles</th>
                        <th className="text-right px-4 py-2.5">Route pay</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {g.rows.map((r) => (
                        <tr key={r.id} data-testid={`period-row-${r.id}`}>
                          <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{ddmmyyyy(r.work_date)}</td>
                          <td className="px-4 py-2 font-mono text-xs">{r.route || "—"}</td>
                          <td className="px-4 py-2 text-right">{r.stops}</td>
                          <td className="px-4 py-2 text-right">{r.parcels}</td>
                          <td className="px-4 py-2 text-right font-mono text-xs text-slate-500">{r.mileage ? Number(r.mileage).toFixed(1) : "—"}</td>
                          <td className="px-4 py-2 text-right font-heading font-bold">{r.route_payment != null ? gbp(r.route_payment) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PayPeriodDetail;
